import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo, useState } from 'react'

import {
	preloadGetPointOfSale,
	preloadGetPointOfSaleDeviceStatus,
	useGetPointOfSale,
	useGetPointOfSaleUnlockPin
} from '@/api/react-query'
import { AccessKeyDevelopmentModeCell, CreateAccessKeyDrawer } from '@/components/access-key/create-access-key-drawer'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { EmptyState } from '@/components/base/empty-state'
import { IconButton } from '@/components/base/icon-button'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { SingleColumnPage } from '@/components/page-layout'
import { DeviceStatusPanel } from '@/components/points-of-sale/device-status-panel'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { Key } from '@/icons/key'
import { Plus } from '@/icons/plus'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/devices/$pointOfSaleUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit-unlock-pin']}>
			<DeviceDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { pointOfSaleUuid } }) => {
		const [pointOfSale] = await Promise.all([
			preloadGetPointOfSale(context, { path: { pointOfSaleUuid } }),
			preloadGetPointOfSaleDeviceStatus(context, { path: { pointOfSaleUuid } })
		])
		return pointOfSale
	},
	staticData: {
		crumb: ({ loaderData }) => loaderData?.name || 'Device'
	}
})

function DeviceDetail() {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()
	const [createAccessKeyOpen, setCreateAccessKeyOpen] = useState(false)

	const pointOfSale = useGetPointOfSale({ pointOfSaleUuid })
	const unlockPin = useGetPointOfSaleUnlockPin({ pointOfSaleUuid })

	const accessKeyColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: 'Type',
				field: 'type',
				flex: 1
			},
			{
				headerName: 'Token',
				field: 'token',
				flex: 3,
				cellRenderer: ({ value, data }: { value?: string; data: any }) => {
					const displayValue = data.type === 'PUBLISHABLE' ? value : data.redacted
					const isPublishable = data.type === 'PUBLISHABLE'
					return (
						<div className='flex h-full items-center gap-2'>
							<code className='bg-muted rounded px-2 py-1 text-xs'>
								{displayValue ? `${displayValue.substring(0, 20)}...` : null}
							</code>
							{isPublishable && value && <Copy content={value} variant='mini' />}
						</div>
					)
				}
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			{
				headerName: L.Settings.AccessKeys.Field.DevelopmentMode(),
				flex: 1,
				cellRenderer: AccessKeyDevelopmentModeCell
			}
		],
		[L]
	)

	if (!pointOfSale.data) {
		return null
	}

	return (
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader heading={pointOfSale.data.name} />
				<SectionRow title={L.Settings.PointOfSale.Column.Identifier()} value={pointOfSale.data.identifier} />
			</Container>

			<Container>
				<ContainerHeader
					heading={L.Settings.PointOfSale.UnlockPin.Title()}
					subHeading={
						<Text size='small' className='text-ui-fg-subtle'>
							{L.Settings.PointOfSale.UnlockPin.Description()}
						</Text>
					}
					rightSide={
						<Link from={Route.fullPath} to='./edit-unlock-pin'>
							<IconButton tooltipText={L.General.Edit()}>
								<Key />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow
					title={L.Settings.PointOfSale.UnlockPin.Pin()}
					value={
						unlockPin.data?.hasPin
							? L.Settings.PointOfSale.UnlockPin.PinSet()
							: L.Settings.PointOfSale.UnlockPin.PinNotSet()
					}
				/>
			</Container>

			<Container>
				<ContainerHeader
					heading={L.Settings.PointOfSale.Column.AccessKeys()}
					rightSide={
						<IconButton tooltipText={L.General.Create()} onClick={() => setCreateAccessKeyOpen(true)}>
							<Plus />
						</IconButton>
					}
				/>
				<Table
					className='w-full'
					data={pointOfSale.data.accessKeys}
					columnUtilities={{}}
					columnDefs={accessKeyColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					emptyState={
						<EmptyState
							title={L.Settings.Devices.AccessKeysEmptyTitle()}
							description={L.Settings.Devices.AccessKeysEmptyDescription()}
							className='min-h-[200px]'
							action={{
								label: L.General.Create(),
								onClick: () => setCreateAccessKeyOpen(true)
							}}
						/>
					}
				/>
			</Container>

			<DeviceStatusPanel pointOfSaleUuid={pointOfSaleUuid} pointOfSaleName={pointOfSale.data.name} />

			<CreateAccessKeyDrawer
				open={createAccessKeyOpen}
				onOpenChange={setCreateAccessKeyOpen}
				pointOfSaleUuid={pointOfSaleUuid}
			/>
		</SingleColumnPage>
	)
}
